/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
import React, { useContext, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import { AccountContext } from '../../../components/AccountContext';
import gql from 'graphql-tag';
import { regenerateUrlWithQuery } from '../../../utils/UrlUtil';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import TeamReportGenerationForm from './TeamReportGenerationForm';
import MembersEditContainer from '../edit/MembersEditContainer';
import { Loading } from '../../../components/Loading';
import EditTeamReportContainer from '../edit/EditTeamReportContainer';
import { useHistory, useParams } from 'react-router-dom';
import { updateTeamReport, deleteTeamReport } from '../../../services/apps/TeamReportService';
import DeleteTeamReport from '../delete/DeleteTeamReport';
import { DeleteTextButton } from '../../../components/button/ActionButton';
import { NotificationContext } from '../../../components/GrowlNotifications';
import TeamReportAssessmentListContainer from './TeamReportAssessmentListContainer';
import useSubscriptionExpiredRestrictions from '../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  plhFile: {
    id: 'res_import.nothing_selected',
    defaultMessage: 'Nothing selected',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  deleteTeamReport: { id: 'del_header.team_report', defaultMessage: 'Delete Team Report' },
  editMembers: { id: 'team_report.edit-members', defaultMessage: 'Add/Remove Members' },
  edit: { id: 'actions.edit', defaultMessage: 'Edit' },
});

const TEAM_REPORT_DETAIL_QUERY = gql`
  query reportDetail($accountId: ID!, $id: ID!) {
    account(id: $accountId) {
      id
      teamReport(id: $id) {
        id
        teamName
        numberOfAssessments
        createdOn
        updatedOn
        template {
          id
          title
        }
      }
    }
  }
`;

const TeamReportDetails = () => {
  const history = useHistory();
  const { reportId } = useParams();
  const { formatMessage } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const DEFAULT_ACCOUNT_STATE = {
    id: null,
    teamReport: {
      id: null,
      teamName: null,
      numberOfAssessments: 0,
      createdOn: null,
      updatedOn: null,
      template: {
        id: null,
        title: null,
      },
    },
  };

  const [mngModal, setManageModal] = useState(false);
  const [infoEditing, setInfoEditing] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const { loading, data: queryData } = useQuery(TEAM_REPORT_DETAIL_QUERY, {
    variables: { accountId: currentAccount, id: reportId },
  });

  useEffect(() => {
    if (queryData) {
      setAccount(queryData.account);
    }
  }, [queryData]);

  const remountTeamList = () => {
    const oldPath = history.location;
    const newPath = regenerateUrlWithQuery(oldPath.pathname, {});
    history.replace(newPath);
  };

  const onUpdate = async (value) => {
    try {
      await updateTeamReport({
        accountId: currentAccount,
        teamReportId: reportId,
        teamName: value.teamName,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  };

  const processDeleting = async () => {
    if (reportId) {
      try {
        await deleteTeamReport({
          accountId: currentAccount,
          teamReportId: reportId,
        });
        const teamReportsURL = `/accounts/${currentAccount}/team-reports`;
        growlActionSuccess(growl, formatMessage(messages.successNoti));
        history.push(teamReportsURL);
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }
    showDeleteConfirmModal(false);
  };

  if (loading || !account || !account.teamReport) {
    return <Loading />;
  }

  const { teamReport } = account;
  const template = teamReport.template.title;
  const templateId = teamReport.template.id;
  const teamReportId = teamReport.id;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <div className="p-col-12 p-sm-12 p-md-6">
              {infoEditing ? (
                <div className="wrapper">
                  <EditTeamReportContainer
                    onCancel={() => setInfoEditing(false)}
                    onUpdate={onUpdate}
                    teamReport={teamReport}
                  />
                </div>
              ) : (
                <Panel
                  header={
                    <div className="p-grid p-nogutter">
                      <div className="p-col">{`${teamReport.teamName} - ${template}`}</div>
                      <div className="p-col-2 align-right">
                        <Button
                          type="button"
                          icon="fa fa-pencil"
                          className="p-button-secondary"
                          onClick={() => setInfoEditing(true)}
                          disabled={subscriptionIsExpired}
                          tooltip={formatMessage(messages.edit)}
                          tooltipOptions={{ position: 'bottom' }}
                        />
                      </div>
                    </div>
                  }
                >
                  <div className="p-grid">
                    <div className="p-col">
                      <Button
                        label={formatMessage(messages.editMembers)}
                        type="button"
                        icon="fa fa-plus-circle"
                        disabled={subscriptionIsExpired}
                        onClick={() => setManageModal(true)}
                      />
                    </div>
                    <div className="p-col align-right">
                      <DeleteTextButton
                        position="right"
                        onClick={() => setShowDeleteConfirmModal(true)}
                        disabled={subscriptionIsExpired}
                        text={formatMessage(messages.deleteTeamReport)}
                      />
                    </div>
                  </div>
                  <TeamReportGenerationForm loading={loading} teamReportId={teamReportId} />
                </Panel>
              )}

              <MembersEditContainer
                modalName={`${teamReport.teamName}`}
                modalOpen={mngModal}
                hideModal={() => setManageModal(false)}
                template={templateId}
                remount={remountTeamList}
              />
              <DeleteTeamReport
                modalOpen={showDeleteConfirmModal}
                reject={() => setShowDeleteConfirmModal(false)}
                confirm={processDeleting}
                typeButton="btn-danger"
                reportId={reportId}
              />
            </div>
            <div className="p-col-12">
              <TeamReportAssessmentListContainer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamReportDetails;
